// ** React Imports
import { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  CustomInput,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import Select from "react-select";
import { selectThemeColors, getCountryCurrencySymbol } from "@utils";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { Slide, toast } from "react-toastify";
import Flatpickr from "react-flatpickr";
import moment from "moment";

// ** Styles
import "@styles/react/libs/react-select/_react-select.scss";
import "@styles/react/libs/tables/react-dataTable-component.scss";
import "@styles/react/libs/flatpickr/flatpickr.scss";
import SpinnerFlex from "views/components/spinners/SpinnerFlex";
import {
  createSubscription,
  createTopup,
  getKyc,
  getPlans,
  createDailyTalktimeTopup,
  getAllDailyTalktimes,
} from "../store/action/index";
import { Wrapper } from "../../../../components/wrapper/wrapper.component";
import { selectSelectedTeam } from "../store/teams.selector";
import {
  useAddOrgNoteMutation,
  useGetAllPlansQuery,
  useGetSetupChargesPaidListQuery,
  useUpdateOrgMutation,
} from "../../../../redux/api/superfone-api";
import styles from "./style.module.scss";
import { setOrgEmail } from "../../../../redux/slices/notes/notes.api";
import {
  legacy_plan_ids,
  new_plan_ids,
  isPriorityPlan,
} from "../daily-talktime/new-plan-ids";
import AddonsCommon from "./addonsCommon";
import { getUpfrontBillingDays } from "./aiAddonBilling";
import { selectSubscriptionFromId } from "../store/action/teams.selector";

const AI_ADDON_SLUGS = [
  "ai-missed-call-agent",
  "ai-sale-agent",
  "ai-receptionist-agent",
];

const MySwal = withReactContent(Swal);
export const CREATE_DAILY_TALKTIME_NAMES = {
  DAILY_TALKTIME_CHOTA_TOP_UP: "Chota top-up",
  DAILY_TALKTIME_RECURRING_TOP_UP: "Recurring top-up",
};

export const CREATE_DAILY_TALKTIME_TYPES = {
  DAILY_TALKTIME_CHOTA_TOP_UP: "DAILY_TALKTIME_CHOTA_TOP_UP",
  DAILY_TALKTIME_RECURRING_TOP_UP: "DAILY_TALKTIME_RECURRING_TOP_UP",
};

const CreateSubModal = ({
  onDismiss = () => {},
  onClose = () => {},
  onProceed = () => {},
  visible = false,
  team,
  currentSubscription,
  topupOnly = false,
}) => {
  const { data: plans = [] } = useGetAllPlansQuery();
  const { data: setupChargesPaidList, isLoading: isSetupFeesLoading } =
    useGetSetupChargesPaidListQuery(
      { orgId: String(team?.id ?? "") },
      { skip: !team?.id },
    );

  // states
  const is_X_Orgs = team?.is_lite;
  const loaders = useSelector((state) => state.teams.loaders);
  const [selectedVariantId, setSelectedVariantId] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentType, setPaymentType] = useState({
    label: "Razorpay",
    value: "RAZORPAY",
  });
  const [topupType, setTopupType] = useState({
    label: "Chota topup (expires eod)",
    value: CREATE_DAILY_TALKTIME_TYPES.DAILY_TALKTIME_CHOTA_TOP_UP,
  });
  const org = useSelector(selectSelectedTeam);
  const currencySymbol = getCountryCurrencySymbol(team?.country || org?.country);
  const [paymentId, setPaymentId] = useState("");
  const [amount, setAmount] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [orderType, setOrderType] = useState("PURCHASE");
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [active, setActive] = useState(topupOnly ? "TOUPUPV2" : "SUB");
  const [talktime, setTalkTime] = useState(500);
  const [extraFields, setExtraFields] = useState({
    gst: "",
    description: "",
  });
  const [newCombinations, setNewCombinations] = useState([]);
  const [paymentNotCollected, setPaymentNotCollected] = useState(false);
  const [newPlanAddons, setNewPlanAddons] = useState([]);
  const [unmappedAddons, setUnmappedAddons] = useState([]);
  const currentSubscriptionId = currentSubscription?.id;
  const currentActiveSubscription = useSelector((state) =>
    selectSubscriptionFromId(state, currentSubscriptionId),
  );

  // constants (chosen plan, active addons, variant)
  const chosenPlan =
    plans?.find((p) => p.id === Number(selectedPlan?.value || 0)) ||
    plans?.find((p) => p.id === currentActiveSubscription?.plan_id);

  const isRenewal = orderType === "RENEW";
  const isPlanChange =
    !!currentActiveSubscription?.plan_id &&
    !!chosenPlan?.id &&
    Number(chosenPlan.id) !== Number(currentActiveSubscription.plan_id);
  const isAddonsChangeMode = isRenewal || isPlanChange;

  // For renew/plan-change we use `selectedAddons` list (change-plan mode).
  // For purchase we do not pre-select any addons.
  const activeAddons = isRenewal ? currentActiveSubscription?.addons || [] : [];

  const variant = chosenPlan?.variants?.find((v) => v.id === selectedVariantId);

  // merge addon groups
  function mergeAddonGroups(planAddons = [], combinations = []) {
    const result = [];
    const groupedMap = new Map();
    for (const addon of planAddons) {
      if (addon.items) {
        groupedMap.set(addon.slug, { ...addon });
      } else {
        result.push(addon);
      }
    }
    for (const combo of combinations) {
      if (combo.items && groupedMap.has(combo.slug)) {
        const existing = groupedMap.get(combo.slug);
        const mergedItems = [...(existing.items || []), ...(combo.items || [])];
        groupedMap.set(combo.slug, {
          ...existing,
          items: mergedItems,
          id: mergedItems.map((a) => a.id),
          count: mergedItems.length,
          amount: mergedItems.reduce((sum, a) => sum + (a.amount || 0), 0),
          gross_amount: mergedItems.reduce(
            (sum, a) => sum + (a.gross_amount || 0),
            0,
          ),
        });
      } else {
        groupedMap.set(combo.slug, combo);
      }
    }
    result.push(...Array.from(groupedMap.values()));
    return result;
  }

  function mapSubscriptionAddonsToChosenPlan(
    currentAddons = [],
    chosenAddons = [],
  ) {
    const userAddons = currentAddons.filter((a) =>
      (a.slug || "").toLowerCase().includes("users"),
    );
    const automationAddons = currentAddons.filter((a) =>
      (a.slug || "").toLowerCase().includes("automation"),
    );
    const leadStorageAddons = currentAddons.filter((a) =>
      (a.slug || "").toLowerCase().includes("lead-storage"),
    );

    const userCount = userAddons.reduce(
      (sum, addon) => sum + Number(addon.feature_value || 0),
      0,
    );
    const automationCount = automationAddons.reduce(
      (sum, addon) => sum + Number(addon.feature_value || 0),
      0,
    );
    // 10K leads per pack; fall back to row count if feature_value is missing
    const leadStoragePackCount =
      Math.ceil(
        leadStorageAddons.reduce(
          (sum, addon) => sum + Number(addon.feature_value || 0),
          0,
        ) / 10000,
      ) || leadStorageAddons.length;

    const unmapped = [];

    const buildGroupedAddon = (templateSlug, groupItems, count) => {
      const templates = chosenAddons.filter((a) =>
        (a.slug || "").includes(templateSlug),
      );
      if (templates.length === 0 || groupItems.length === 0 || !count)
        return null;
      const items = Array.from({ length: count }, (_, idx) => ({
        ...(templates[idx % templates.length] || {}),
      }));
      return {
        slug: templateSlug,
        type: templateSlug,
        id: items.map((a) => a.id),
        items,
        count,
        name: items[0]?.name,
        gross_amount: items.reduce((sum, a) => sum + (a.gross_amount || 0), 0),
        amount: items.reduce((sum, a) => sum + (a.amount || 0), 0),
      };
    };

    const groupedUserAddon = buildGroupedAddon(
      "users-v2-1",
      userAddons,
      userCount,
    );
    if (!groupedUserAddon && userCount > 0) {
      unmapped.push({
        type: "users-v2-1",
        count: userCount,
        reason: "Users addon not available in selected plan.",
      });
    }

    const groupedAutomationAddon = buildGroupedAddon(
      "automation-v2-pack-of-5",
      automationAddons,
      automationCount / 5,
    );
    if (!groupedAutomationAddon && automationCount > 0) {
      unmapped.push({
        type: "automation-v2-pack-of-5",
        count: automationCount / 5,
        reason: "Automation addon not available in selected plan.",
      });
    }

    const groupedLeadStorageAddon = buildGroupedAddon(
      "lead-storage-extra-10k",
      leadStorageAddons,
      leadStoragePackCount,
    );
    if (!groupedLeadStorageAddon && leadStoragePackCount > 0) {
      unmapped.push({
        type: "lead-storage-extra-10k",
        count: leadStoragePackCount,
        reason: "Lead storage addon not available in selected plan.",
      });
    }

    const otherCurrentAddons = currentAddons.filter(
      (a) =>
        !(a.slug || "").toLowerCase().includes("users") &&
        !(a.slug || "").toLowerCase().includes("automation") &&
        !(a.slug || "").toLowerCase().includes("lead-storage"),
    );

    const mappedOtherAddons = [];
    otherCurrentAddons.forEach((current) => {
      const byId = chosenAddons.find((c) => c.id === current.id);
      if (byId) {
        mappedOtherAddons.push(byId);
        return;
      }
      const bySlug = chosenAddons.find((c) => c.slug === current.slug);
      if (bySlug) {
        mappedOtherAddons.push(bySlug);
        return;
      }
      unmapped.push({
        type: current.slug || "",
        count: 1,
        reason: `Addon "${current.name}" not available in selected plan.`,
      });
    });

    const finalAddons = [
      groupedUserAddon,
      groupedAutomationAddon,
      groupedLeadStorageAddon,
      ...mappedOtherAddons,
    ].filter(Boolean);

    return { finalAddons, unmapped };
  }

  // Map current subscription addons to selected plan when renewing or changing plan
  useEffect(() => {
    if (!isAddonsChangeMode) {
      setNewPlanAddons([]);
      setUnmappedAddons([]);
      return;
    }
    if (!chosenPlan || !currentActiveSubscription) {
      return;
    }
    const currentAddons = currentActiveSubscription.addons || [];
    if (currentAddons.length === 0) {
      setNewPlanAddons([]);
      setUnmappedAddons([]);
      return;
    }
    const { finalAddons, unmapped } = mapSubscriptionAddonsToChosenPlan(
      currentAddons,
      chosenPlan?.addons || [],
    );
    setNewPlanAddons(finalAddons);
    setUnmappedAddons(unmapped);
  }, [isAddonsChangeMode, chosenPlan, currentActiveSubscription]);

  // intitial data
  useEffect(() => {
    if (org.gstin) {
      setExtraFields((extra) => ({
        ...extra,
        gst: org.gstin,
      }));
    }
  }, [org.gstin]);

  // handlers
  const handlePaymentIdChange = (e) => {
    setPaymentId(e.target.value);
  };

  const clearData = () => {
    setSelectedPlan(null);
    setSelectedVariantId(0);
    setPaymentId("");
    setStartDate(null);
    setEndDate(null);
    setSelectedAddons([]);
    setNewPlanAddons([]);
    setNewCombinations([]);
    setUnmappedAddons([]);
    setExtraFields({
      gst: "",
      description: "",
    });
    setTalkTime(500);
    setAmount("");
  };

  const handleOnClose = () => {
    onClose();
    clearData();
  };

  const handleOnDismiss = () => {
    onDismiss();
    clearData();
  };

  const onSubmit = () => {
    const setupFeeAddonsPayload =
      !isSetupFeesLoading && Array.isArray(newSelectedAddons)
        ? newSelectedAddons
            .filter((addon) => AI_ADDON_SLUGS.includes(addon?.slug))
            .filter((addon) => {
              const featureId = addon?.feature_id;
              const setupFeeGross = addon?.setup_fee_gross_amount;
              if (!featureId || !setupFeeGross || setupFeeGross <= 0)
                return false;
              const isPaidForFeature = setupChargesPaidList?.some(
                (fee) => fee.feature_id === featureId,
              );
              return !isPaidForFeature;
            })
            .map((addon) => ({
              id: addon.id,
              setup_fees_paid: false,
            }))
        : [];

    if (active === "TOUPUPV2") {
      onProceed({
        payment_id: paymentNotCollected ? null : paymentId.trim(),
        plan: chosenPlan,
        ...(paymentType?.value ? { payment_type: paymentType.value } : {}),
        ...(amount ? { amount: paymentNotCollected ? 0 : Number(amount) } : {}),
        variant,
        start_date: startDate
          ? moment(startDate).startOf("day").toDate()
          : moment().startOf("day").toDate(),
        order_type: orderType,
        add_addons: newSelectedAddons.flatMap((s) =>
          Array.isArray(s.id) ? s.id : [s.id],
        ),
        addons: newSelectedAddons.flatMap((s) =>
          Array.isArray(s.id) ? s.id : [s.id],
        ),
        remove_addons: [],
        talktime: talktime,
        type: active,
        ...(extraFields?.gst ? { gstin: extraFields.gst } : {}),
        ...(extraFields?.description
          ? { description: extraFields.description }
          : {}),
        topup_type: topupType.value,
        ...(setupFeeAddonsPayload.length
          ? { setup_fee_addons: setupFeeAddonsPayload }
          : {}),
        end_date: endDate
          ? moment(endDate).endOf("day").toDate()
          : moment().endOf("day").toDate(),
      });
    } else {
      onProceed({
        payment_id: paymentNotCollected ? null : paymentId.trim(),
        plan: chosenPlan,
        ...(paymentType?.value ? { payment_type: paymentType.value } : {}),
        ...(amount ? { amount: paymentNotCollected ? 0 : Number(amount) } : {}),
        variant,
        start_date: startDate
          ? moment(startDate).startOf("day").toDate()
          : null,
        order_type: orderType,
        add_addons: newSelectedAddons.flatMap((s) =>
          Array.isArray(s.id) ? s.id : [s.id],
        ),
        addons: newSelectedAddons.flatMap((s) =>
          Array.isArray(s.id) ? s.id : [s.id],
        ),
        remove_addons: [],
        talktime: talktime,
        type: active,
        ...(extraFields?.gst ? { gstin: extraFields.gst } : {}),
        ...(extraFields?.description
          ? { description: extraFields.description }
          : {}),
        ...(setupFeeAddonsPayload.length
          ? { setup_fee_addons: setupFeeAddonsPayload }
          : {}),
      });
    }
    clearData();
  };

  const handleOnProceed = () => {
    if (
      currentSubscription?.plan?.name?.toLowerCase()?.includes("standard") &&
      chosenPlan?.slug?.toLowerCase()?.includes("free-tier")
    ) {
      MySwal.fire({
        title: "Cannot give CRM/WABA with standard phone plan",
        text: "",
      });
      return;
    }
    if (!extraFields?.gst) {
      MySwal.fire({
        icon: "warning",
        title: "No GST",
        text: `No GST invoice will be given if no GST is entered. Sure to go ahead?`,
        confirmButtonText: "Go ahead without GST",
        showCancelButton: true,
        customClass: {
          confirmButton: "btn btn-warning",
          cancelButton: "btn btn-secondary ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          onSubmit();
        }
      });
    } else {
      onSubmit();
    }
  };

  const onChangePlan = (selection) => {
    setSelectedPlan(selection);
    setSelectedAddons([]);
    setNewPlanAddons([]);
    setNewCombinations([]);
    setUnmappedAddons([]);

    const chosenPlan = plans?.find((p) => p.id === Number(selection.value));
    setSelectedVariantId(chosenPlan?.variants?.[0]?.id || 0);
  };

  const setPicker = (date) => {
    setStartDate(date[0]);
  };

  const setPickerEndDate = (date) => {
    setEndDate(date[0]);
  };

  // constants (addons price calculations) — mirror changePaidPlan logic
  const newSelectedAddons = isAddonsChangeMode
    ? mergeAddonGroups(newPlanAddons, newCombinations)
    : [...(selectedAddons ?? []), ...(newCombinations ?? [])];

  // AI missed-call: prorate with 30-day cap.
  // AI sale & receptionist: quarterly/half-yearly = full amount for the whole
  // cycle; monthly = prorate with 30-day cap.
  const round2 = (num) => Math.round(num * 100) / 100;
  const monthFactor = variant?.revenue_duration
    ? (Math.round(variant.revenue_duration / 30) * 100) / 100
    : 0;
  const variantDurationDays = variant?.revenue_duration || 0;
  const aiAgentDays = Math.min(variantDurationDays, 30);
  const aiAgentMonthFactor = Math.round((aiAgentDays / 30) * 100) / 100;

  let addonsGrossPrice = 0;
  let addonsTaxTotal = 0;
  let aiReceptionistAgentDays = 0;
  let aiSalesAgentDays = 0;
  newSelectedAddons.forEach((addon) => {
    const isAIMissedCallAgent = addon.slug === "ai-missed-call-agent";
    const isAISalesAgent = addon.slug === "ai-sale-agent";
    const isAIReceptionistAgent = addon.slug === "ai-receptionist-agent";
    const upfrontDays = getUpfrontBillingDays(addon);

    let factor;
    if (isAIMissedCallAgent) {
      factor = aiAgentMonthFactor;
    } else if (isAISalesAgent) {
      factor = upfrontDays ? 1 : aiAgentMonthFactor;
      aiSalesAgentDays = upfrontDays ?? aiAgentDays;
    } else if (isAIReceptionistAgent) {
      factor = upfrontDays ? 1 : aiAgentMonthFactor;
      aiReceptionistAgentDays = upfrontDays ?? aiAgentDays;
    } else {
      factor = monthFactor;
    }

    const grossRupees = round2(
      (Number(addon.gross_amount || 0) / 100) * factor,
    );
    addonsGrossPrice += grossRupees;
  });

  const taxRate = (variant?.tax_rate || 0) / 100;
  const addonGst = round2(addonsGrossPrice * taxRate);
  const addonsPrice = round2(addonsGrossPrice + addonGst);

  // Charge setup fee once per unpaid feature_id
  const chargedFeatureIds = new Set();
  const { setupFeeGrossPaise, setupFeeGstPaise } =
    !isSetupFeesLoading && Array.isArray(newSelectedAddons)
      ? newSelectedAddons
          .filter((addon) => AI_ADDON_SLUGS.includes(addon?.slug))
          .reduce(
            (acc, addon) => {
              const featureId = addon?.feature_id;
              const setupFeeGross = addon?.setup_fee_gross_amount;
              if (!featureId || !setupFeeGross || setupFeeGross <= 0) return acc;
              if (chargedFeatureIds.has(featureId)) return acc;
              const isPaidForFeature = setupChargesPaidList?.some(
                (fee) => fee.feature_id === featureId,
              );
              if (isPaidForFeature) return acc;
              chargedFeatureIds.add(featureId);
              const count = addon?.count ?? 1;
              return {
                setupFeeGrossPaise: acc.setupFeeGrossPaise + setupFeeGross * count,
                setupFeeGstPaise:
                  acc.setupFeeGstPaise +
                  setupFeeGross * ((variant?.tax_rate || 0) / 100) * count,
              };
            },
            { setupFeeGrossPaise: 0, setupFeeGstPaise: 0 },
          )
      : { setupFeeGrossPaise: 0, setupFeeGstPaise: 0 };
  const setupFeeTotalRupeesWithGst = round2(
    (setupFeeGrossPaise + setupFeeGstPaise) / 100,
  );
  const totalPayableWithSetupFee = round2(
    (variant?.amount || 0) / 100 + addonsPrice + setupFeeTotalRupeesWithGst,
  );

  const priorityPlans = plans.filter((p) => isPriorityPlan(p));
  const nonPriorityPlans = plans.filter((p) => !isPriorityPlan(p));

  return (
    <div className="demo-inline-spacing">
      <div>
        <Modal scrollable isOpen={visible} toggle={handleOnClose}>
          <ModalHeader toggle={handleOnClose}>
            {topupOnly ? "Talktime Topup" : "Create Plan"} for team id: {team.id} name: ({team.name})
          </ModalHeader>
          {loaders.plans ? (
            <SpinnerFlex />
          ) : (
            <>
              <ModalBody style={{ height: "800px" }}>
                <Nav tabs style={{ display: "flex", justifyContent: "center" }}>
                  {/* Plans are created via V3 Order; the topup-only entry keeps just the pulse tabs. */}
                  <NavItem hidden={topupOnly}>
                    <NavLink
                      active={active === "SUB"}
                      onClick={() => {
                        clearData();
                        setActive("SUB");
                      }}
                    >
                      Create Plan
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      active={active === "TOUPUP"}
                      onClick={() => {
                        clearData();
                        setActive("TOUPUP");
                      }}
                    >
                      Add Pulse
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      active={active === "TOUPUPV2"}
                      onClick={() => {
                        clearData();
                        setActive("TOUPUPV2");
                      }}
                    >
                      Add Daily Pulse Minutes
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent className="py-50" activeTab={active}>
                  <TabPane tabId="SUB">
                    <>
                      <Form
                        className="auth-register-form mt-2"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <FormGroup className="mb-1">
                          <div className="d-flex align-items-center user-total-numbers">
                            <div className="d-flex align-items-center mr-2">
                              <div>
                                <p className="mb-0">Order Type:</p>
                              </div>
                              <div className="ml-1 color-box">
                                <CustomInput
                                  type="checkbox"
                                  className="custom-control-Primary"
                                  id="purchase-checkbox"
                                  label="PURCHASE"
                                  onChange={(e) => setOrderType("PURCHASE")}
                                  checked={orderType === "PURCHASE"}
                                />
                              </div>
                              <div className="ml-1 color-box">
                                <CustomInput
                                  type="checkbox"
                                  className="custom-control-Primary"
                                  id="renewal-checkbox"
                                  label="RENEWAL"
                                  onChange={(e) => {
                                    setOrderType("RENEW");
                                    if (
                                      currentActiveSubscription?.plan_id &&
                                      plans?.length
                                    ) {
                                      const currentPlan = plans.find(
                                        (p) =>
                                          p.id ===
                                          currentActiveSubscription.plan_id,
                                      );
                                      if (currentPlan) {
                                        setSelectedPlan({
                                          value: String(currentPlan.id),
                                          label: currentPlan.description,
                                        });
                                        setSelectedVariantId(
                                          currentPlan?.variants?.[0]?.id || 0,
                                        );
                                      }
                                    }
                                  }}
                                  checked={orderType === "RENEW"}
                                />
                              </div>
                            </div>
                          </div>
                        </FormGroup>
                        <Wrapper padding="1rem 0" fullWidth type="vertical">
                          <div className={styles["custom-select"]}>
                            {is_X_Orgs ? (
                              <div className="tw-text-xs tw-text-black tw-mb-1">
                                <span className="tw-font-bold">Note:</span>{" "}
                                Showing Only X Plans
                              </div>
                            ) : (
                              <div className="tw-text-xs tw-text-black tw-mb-1">
                                <span className="tw-font-bold">Note:</span>{" "}
                                Showing only SF Plans
                              </div>
                            )}
                            <Select
                              theme={selectThemeColors}
                              isClearable={false}
                              className="react-select"
                              classNamePrefix="select"
                              options={
                                is_X_Orgs
                                  ? [
                                      {
                                        label: "Plans",
                                        options: plans
                                          .filter(
                                            (p) =>
                                              Array.isArray(p.products) &&
                                              p.products.includes(
                                                "SUPERFONE_X",
                                              ),
                                          )
                                          .map((p) => ({
                                            value: p.id,
                                            label: p.description,
                                          })),
                                      },
                                    ]
                                  : [
                                      {
                                        label: "Plans",
                                        options: priorityPlans
                                          .filter(
                                            (p) =>
                                              !(
                                                Array.isArray(p.products) &&
                                                p.products.includes(
                                                  "SUPERFONE_X",
                                                )
                                              ),
                                          )
                                          .map((p) => ({
                                            value: p.id,
                                            label: p.description,
                                          })),
                                      },
                                      {
                                        label: "Legacy Plans",
                                        options: nonPriorityPlans
                                          .filter(
                                            (p) =>
                                              !(
                                                Array.isArray(p.products) &&
                                                p.products.includes(
                                                  "SUPERFONE_X",
                                                )
                                              ),
                                          )
                                          .map((p) => ({
                                            value: p.id,
                                            label: p.description,
                                          })),
                                      },
                                    ]
                              }
                              value={selectedPlan}
                              placeholder="Choose Subscription Plan"
                              onChange={onChangePlan}
                            />
                          </div>
                        </Wrapper>
                        {selectedPlan ? (
                          <div className="tw-flex tw-gap-4 tw-items-center tw-pb-4 tw-flex-wrap">
                            {chosenPlan?.variants?.map((v) => {
                              return (
                                <div
                                  key={v.id}
                                  className="tw-cursor-pointer tw-border tw-border-solid tw-p-2 tw-rounded-lg tw-min-w-[33%] tw-border-black/30 tw-flex tw-gap-2 tw-items-start"
                                  onClick={() => {
                                    setSelectedVariantId(v.id);
                                  }}
                                >
                                  <CustomInput
                                    id={v.id}
                                    type="radio"
                                    checked={selectedVariantId === v.id}
                                  />
                                  <div className="tw-flex tw-flex-col tw-gap-1 tw-items-start">
                                    <div className="tw-font-semibold">
                                      {v.name}
                                    </div>
                                    <div>{`${currencySymbol} ${v.amount / 100}`}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : null}
                        <div
                          className="tw-py-4 tw-cursor-pointer"
                          onClick={() => {
                            setPaymentNotCollected(!paymentNotCollected);
                            if (!paymentNotCollected) {
                              setAmount("");
                              setPaymentId("");
                            }
                          }}
                        >
                          <CustomInput
                            id="paymentNotCollected"
                            type="checkbox"
                            checked={paymentNotCollected}
                            className="tw-pointer-events-none"
                          >
                            <strong className="tw-select-none">
                              Payment not collected
                            </strong>
                          </CustomInput>
                        </div>
                        <div
                          className={`tw-pb-4 ${
                            paymentNotCollected
                              ? "tw-opacity-50 tw-grayscale tw-pointer-events-none"
                              : ""
                          }`}
                        >
                          <Select
                            theme={selectThemeColors}
                            isClearable={false}
                            className="react-select"
                            classNamePrefix="select"
                            options={[
                              {
                                label: "Razorpay",
                                value: "RAZORPAY",
                              },
                              {
                                label: "Bank Transfer",
                                value: "BANK_TRANSFER",
                              },
                            ]}
                            value={paymentType}
                            placeholder="Choose Payment Type"
                            onChange={(paymentType) => {
                              setPaymentType(paymentType);
                            }}
                          />
                        </div>
                        {paymentType?.value === "BANK_TRANSFER" ? (
                          <div
                            className={`tw-pb-4 ${
                              paymentNotCollected
                                ? "tw-opacity-50 tw-grayscale tw-pointer-events-none"
                                : ""
                            }`}
                          >
                            <Input
                              autoFocus
                              type="number"
                              value={amount}
                              placeholder="Enter rupees"
                              id="p-amount-id"
                              name="p-amount-name"
                              onChange={(e) => {
                                setAmount(e?.target?.value || "");
                              }}
                            />
                          </div>
                        ) : null}

                        <FormGroup
                          className={`tw-pb-4 ${
                            paymentNotCollected
                              ? "tw-opacity-50 tw-grayscale tw-pointer-events-none"
                              : ""
                          }`}
                        >
                          <Input
                            autoFocus
                            type="text"
                            value={paymentId}
                            placeholder="Enter Payment Id"
                            id="p-id"
                            name="p-id"
                            onChange={handlePaymentIdChange}
                          />
                          <Label className="form-label" for="p-id">
                            Enter Payment Id (Razorpay/Bank Transfer)
                            <div>
                              <small>
                                if amount paid is equal to subscription plan
                                amount
                              </small>
                            </div>
                          </Label>
                        </FormGroup>

                        <FormGroup>
                          <Label className="form-label" for="p-id">
                            Select Plan Start date (Optional)
                          </Label>
                          <Flatpickr
                            value={startDate || new Date()}
                            onChange={(date) => setPicker(date)}
                            className="form-control invoice-edit-input date-picker"
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label className="form-label">GST</Label>
                          <Input
                            autoFocus
                            type="text"
                            {...(org.gstin ? { readOnly: org.gstin } : {})}
                            value={extraFields.gst || org.gstin}
                            placeholder="Enter GST"
                            id="p-cs-gst-id"
                            name="p-cs-gst-name"
                            onChange={(e) => {
                              setExtraFields({
                                ...extraFields,
                                gst: e?.target?.value || "",
                              });
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label className="form-label">Description</Label>
                          <Input
                            type="textarea"
                            value={extraFields.description || ""}
                            placeholder="Enter Detailed Description"
                            id="p-cs-description-id"
                            name="p-cs-description-name"
                            onChange={(e) => {
                              setExtraFields({
                                ...extraFields,
                                description: e?.target?.value || "",
                              });
                            }}
                          />
                        </FormGroup>
                        <div style={{ fontSize: "16px", fontWeight: "600" }}>
                          Addons
                        </div>
                        {unmappedAddons?.length > 0 && (
                          <div className="mb-2">
                            <strong style={{ color: "red" }}>
                              Some addons could not be mapped:
                            </strong>
                            <div className="ml-1">
                              {unmappedAddons.map((addon, i) => (
                                <li key={i}>
                                  {addon.type} ({addon.count}): {addon.reason}
                                </li>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* addons — same flow as changePaidPlan when RENEW */}
                        <AddonsCommon
                          currencySymbol={currencySymbol}
                          plan={chosenPlan}
                          activeAddons={isAddonsChangeMode ? [] : activeAddons}
                          selectedAddons={
                            isAddonsChangeMode ? newPlanAddons : selectedAddons
                          }
                          setSelectedAddons={
                            isAddonsChangeMode
                              ? setNewPlanAddons
                              : setSelectedAddons
                          }
                          setNewCombinations={setNewCombinations}
                          remainingDays={variant?.revenue_duration ?? undefined}
                          type={isAddonsChangeMode ? "change-plan" : undefined}
                        />
                        {newSelectedAddons.length > 0 && (
                          <div style={{ margin: "10px 0", fontWeight: "600" }}>
                            Selected Addons:
                          </div>
                        )}
                        {newSelectedAddons.length > 0 &&
                          newSelectedAddons.map((a, idx) => (
                            <span
                              key={a.slug + idx}
                              style={{ color: "#4CAF50" }}
                            >
                              {a.slug.includes("users-v2-1")
                                ? `Users (${a.count})`
                                : a.slug.includes("automation-v2-pack-of-5")
                                  ? `Automations (${a.count})`
                                  : a.slug.includes("lead-storage")
                                    ? `Lead storage (${a.count * 10}K)`
                                    : a.name}
                              {idx < newSelectedAddons.length - 1 ? ", " : ""}
                            </span>
                          ))}
                        <FormGroup>
                          <div
                            className="tw-text-[14px] tw-leading-[18px] tw-font-semibold "
                            style={{ marginTop: "10px" }}
                          >
                            Total Plan amount: {currencySymbol}{" "}
                            {variant?.amount
                              ? (variant.amount / 100).toFixed(2)
                              : 0}
                          </div>
                          {Array.isArray(newSelectedAddons) &&
                          newSelectedAddons.length ? (
                            <>
                              <div className="tw-text-[14px] tw-leading-[18px] tw-font-semibold">
                                Total Addons amount (including GST):{" "}
                                {currencySymbol}{" "}
                                {Array.isArray(newSelectedAddons) &&
                                newSelectedAddons.length
                                  ? addonsPrice.toFixed(2)
                                  : 0}
                              </div>
                            </>
                          ) : null}
                          {setupFeeGrossPaise > 0 && (
                            <div className="tw-text-[14px] tw-leading-[18px] tw-font-semibold">
                              Setup Fees (First Time Only including GST):{" "}
                              {currencySymbol} {setupFeeTotalRupeesWithGst.toFixed(2)}
                            </div>
                          )}
                          <div
                            style={{
                              fontSize: "14px",
                              lineHeight: "18px",
                              fontWeight: "600",
                            }}
                          >
                            Total Payable Amount : {currencySymbol}{" "}
                            {Math.round(totalPayableWithSetupFee)}
                            {` (${currencySymbol} ${((variant?.amount || 0) / 100).toFixed(2)}${
                              addonsPrice
                                ? ` + ${currencySymbol} ${addonsPrice.toFixed(2)}`
                                : ""
                            }${
                              setupFeeGrossPaise > 0
                                ? ` + ${currencySymbol} ${setupFeeTotalRupeesWithGst.toFixed(2)} setup`
                                : ""
                            })`}
                          </div>
                        </FormGroup>
                      </Form>
                    </>
                  </TabPane>
                </TabContent>
                <TabContent className="py-50" activeTab={active}>
                  <TabPane tabId="TOUPUP">
                    <>
                      <Form
                        className="auth-register-form mt-2"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <FormGroup>
                          <Label className="form-label" for="p-id">
                            Incoming Pulse Minutes to be Added
                          </Label>
                          <Input
                            autoFocus
                            type="number"
                            value={talktime}
                            placeholder="1"
                            min={500}
                            max={10000}
                            step={500}
                            id="p-id"
                            name="p-id"
                            onChange={(e) => {
                              let value = e?.target?.value;
                              value = Math.ceil(value / 500) * 500;
                              setTalkTime(value);
                            }}
                          />
                        </FormGroup>
                        <div className="tw-pb-4">
                          <Select
                            theme={selectThemeColors}
                            isClearable={false}
                            className="react-select"
                            classNamePrefix="select"
                            options={[
                              {
                                label: "Razorpay",
                                value: "RAZORPAY",
                              },
                              {
                                label: "Bank Transfer",
                                value: "BANK_TRANSFER",
                              },
                            ]}
                            value={paymentType}
                            placeholder="Choose Payment Type"
                            onChange={(paymentType) => {
                              setPaymentType(paymentType);
                            }}
                          />
                        </div>
                        {paymentType?.value === "BANK_TRANSFER" ? (
                          <div className="tw-pb-4">
                            <Input
                              autoFocus
                              type="number"
                              value={amount}
                              placeholder="Enter rupees"
                              id="p-amount-id"
                              name="p-amount-name"
                              onChange={(e) => {
                                setAmount(e?.target?.value || "");
                              }}
                            />
                          </div>
                        ) : null}
                        <div
                          style={{
                            fontSize: "14px",
                            lineHeight: "18px",
                            fontWeight: "600",
                            marginBottom: "1rem",
                          }}
                        >
                          Price :
                          {paymentType?.value === "BANK_TRANSFER" ? (
                            <span> {currencySymbol} {amount}</span>
                          ) : (
                            <span> {currencySymbol} {talktime * 0.708}</span>
                          )}
                        </div>
                        {paymentType?.value === "BANK_TRANSFER" ? (
                          <div
                            style={{
                              fontSize: "14px",
                              lineHeight: "18px",
                              marginBottom: "1rem",
                              color: "rgba(0,0,0,0.7)",
                            }}
                          >
                            Estimated Price :
                            <span> {currencySymbol} {talktime * 0.708}</span>
                          </div>
                        ) : null}
                        <FormGroup>
                          <Input
                            autoFocus
                            type="text"
                            value={paymentId}
                            placeholder="Enter Payment Id"
                            id="p-id"
                            name="p-id"
                            onChange={handlePaymentIdChange}
                          />
                          <Label className="form-label" for="p-id">
                            Enter Payment Id (Razorpay/Bank Transfer)
                            <div>
                              <small>
                                if amount paid is equal to subscription plan
                                amount
                              </small>
                            </div>
                          </Label>
                        </FormGroup>
                        <FormGroup>
                          <Label className="form-label">GST</Label>
                          <Input
                            value={extraFields.gst || ""}
                            placeholder="Enter GST"
                            id="p-topup-gst-id"
                            name="p-topup-gst-name"
                            onChange={(e) => {
                              setExtraFields({
                                ...extraFields,
                                gst: e?.target?.value || "",
                              });
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label className="form-label">Description</Label>
                          <Input
                            type="textarea"
                            value={extraFields.description}
                            placeholder="Enter Detailed Description"
                            id="p-topup-description-id"
                            name="p-topup-description-name"
                            onChange={(e) => {
                              setExtraFields({
                                ...extraFields,
                                description: e?.target?.value || "",
                              });
                            }}
                          />
                        </FormGroup>
                      </Form>
                    </>
                  </TabPane>
                </TabContent>
                <TabContent className="py-50" activeTab={active}>
                  <TabPane tabId="TOUPUPV2">
                    <>
                      <Form
                        className="auth-register-form mt-2"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <div className="tw-pb-4">
                          <Select
                            theme={selectThemeColors}
                            isClearable={false}
                            className="react-select"
                            classNamePrefix="select"
                            options={[
                              {
                                label: "Chota topup (expires eod)",
                                value:
                                  CREATE_DAILY_TALKTIME_TYPES.DAILY_TALKTIME_CHOTA_TOP_UP,
                              },
                              {
                                label: "Recurring topup",
                                value:
                                  CREATE_DAILY_TALKTIME_TYPES.DAILY_TALKTIME_RECURRING_TOP_UP,
                              },
                            ]}
                            value={topupType}
                            placeholder="Choose Topup Type"
                            onChange={(topupType) => {
                              setTopupType(topupType);
                            }}
                          />
                        </div>
                        <FormGroup>
                          <Label className="form-label" for="p-id">
                            Outgoing Daily Pulse Minutes to be Added (Minutes)
                          </Label>
                          <Input
                            autoFocus
                            type="number"
                            value={talktime}
                            placeholder="Minutes"
                            min={1}
                            max={10000}
                            step={1}
                            id="p-id"
                            name="p-id"
                            onChange={(e) => {
                              let value = e?.target?.value;
                              value = Math.ceil(value / 1) * 1;
                              setTalkTime(parseInt(value));
                            }}
                          />
                        </FormGroup>
                        {topupType.value !==
                        CREATE_DAILY_TALKTIME_TYPES.DAILY_TALKTIME_CHOTA_TOP_UP ? (
                          <>
                            <FormGroup>
                              <Label className="form-label" for="p-id">
                                Select Start date (Optional)
                              </Label>
                              <Flatpickr
                                value={startDate || new Date()}
                                onChange={(date) => setPicker(date)}
                                className="form-control invoice-edit-input date-picker"
                              />
                            </FormGroup>
                            <FormGroup aria-required>
                              <Label className="form-label" for="ed-id">
                                Select End date
                              </Label>
                              <Flatpickr
                                value={endDate || new Date()}
                                onChange={(date) => setPickerEndDate(date)}
                                className="form-control invoice-edit-input date-picker"
                              />
                            </FormGroup>
                          </>
                        ) : null}
                        <div className="tw-pb-4">
                          <Select
                            theme={selectThemeColors}
                            isClearable={false}
                            className="react-select"
                            classNamePrefix="select"
                            options={[
                              {
                                label: "Razorpay",
                                value: "RAZORPAY",
                              },
                              {
                                label: "Bank Transfer",
                                value: "BANK_TRANSFER",
                              },
                            ]}
                            value={paymentType}
                            placeholder="Choose Payment Type"
                            onChange={(paymentType) => {
                              setPaymentType(paymentType);
                            }}
                          />
                        </div>
                        {paymentType?.value === "BANK_TRANSFER" ? (
                          <div className="tw-pb-4">
                            <Input
                              autoFocus
                              type="number"
                              value={amount}
                              placeholder="Enter rupees"
                              id="p-amount-id"
                              name="p-amount-name"
                              onChange={(e) => {
                                setAmount(e?.target?.value || "");
                              }}
                            />
                          </div>
                        ) : null}
                        <div
                          style={{
                            fontSize: "14px",
                            lineHeight: "18px",
                            fontWeight: "600",
                            marginBottom: "1rem",
                          }}
                        >
                          {paymentType?.value === "BANK_TRANSFER" ? (
                            <span>Price : {currencySymbol} {amount}</span>
                          ) : null}
                        </div>
                        {paymentType?.value === "BANK_TRANSFER" ? (
                          <div
                            style={{
                              fontSize: "14px",
                              lineHeight: "18px",
                              marginBottom: "1rem",
                              color: "rgba(0,0,0,0.7)",
                            }}
                          >
                            Estimated Price :
                            <span>
                              {" "}
                              {currencySymbol}{" "}
                              {(talktime / 60) *
                                15 *
                                Math.ceil(
                                  moment(endDate).diff(
                                    moment(startDate),
                                    "day",
                                    true,
                                  ),
                                )}
                            </span>
                          </div>
                        ) : null}

                        <FormGroup>
                          <Input
                            autoFocus
                            type="text"
                            value={paymentId}
                            placeholder="Enter Payment Id"
                            id="p-id"
                            name="p-id"
                            onChange={handlePaymentIdChange}
                          />
                          <Label className="form-label" for="p-id">
                            Enter Payment Id (Razorpay/Bank Transfer)
                          </Label>
                        </FormGroup>
                        <FormGroup>
                          <Label className="form-label">GST</Label>
                          <Input
                            value={extraFields.gst || ""}
                            placeholder="Enter GST"
                            id="p-topup-gst-id"
                            name="p-topup-gst-name"
                            onChange={(e) => {
                              setExtraFields({
                                ...extraFields,
                                gst: e?.target?.value || "",
                              });
                            }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label className="form-label">Description</Label>
                          <Input
                            type="textarea"
                            value={extraFields.description}
                            placeholder="Enter Detailed Description"
                            id="p-topup-description-id"
                            name="p-topup-description-name"
                            onChange={(e) => {
                              setExtraFields({
                                ...extraFields,
                                description: e?.target?.value || "",
                              });
                            }}
                          />
                        </FormGroup>
                      </Form>
                    </>
                  </TabPane>
                </TabContent>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={handleOnDismiss}>
                  DISMISS
                </Button>
                <Button color="primary" onClick={handleOnProceed}>
                  PROCEED
                </Button>
              </ModalFooter>
            </>
          )}
        </Modal>
      </div>
    </div>
  );
};

const CreateSubscription = ({ team, subscriptions, topupOnly = false }) => {
  const currentSubscription = useMemo(() => {
    return subscriptions?.find((s) => s.status === "active");
  }, [subscriptions]);
  // ** Store Vars
  const dispatch = useDispatch();
  const [shouldShowCreateSubModal, setShowCreateSubModal] = useState(false);

  const handleOnClickCreateSub = () => {
    setShowCreateSubModal(true);
    dispatch(getPlans());
    dispatch(getKyc(team.id));
  };

  const onCreateSubInputComplete = (data) => {
    handleCreateSubscription(team, data);
    setShowCreateSubModal(false);
  };

  const onCloseModel = () => {
    setShowCreateSubModal(false);
  };
  const [updateOrg] = useUpdateOrgMutation();
  const [addOrgNote] = useAddOrgNoteMutation();

  const handleCreateSubscription = async (team, subData) => {
    const { plan, variant, order_type, type, payment_type, amount } = subData;
    if (type === "TOUPUP") {
      return MySwal.fire({
        icon: "warning",
        title: "Are you sure?",
        text: `You want to ${subData.talktime} minutes for team id: ${team.id} name: ${team.name} with Payment Type ${payment_type}`,
        confirmButtonText: "YES! CREATE",
        showCancelButton: true,
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-secondary ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          toast.info("Creating Topup...Please wait", {
            transition: Slide,
            hideProgressBar: true,
            autoClose: 2000,
          });
          dispatch(createTopup(team, subData, (err) => {}));
        }
      });
    }
    if (type === "TOUPUPV2") {
      return MySwal.fire({
        icon: "warning",
        title: "Are you sure?",
        text: `You want to daily ${subData.talktime} minutes for team id: ${team.id} name: ${team.name} with Payment Type ${payment_type}`,
        confirmButtonText: "YES! CREATE",
        showCancelButton: true,
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-secondary ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          toast.info("Creating Topup...Please wait", {
            transition: Slide,
            hideProgressBar: true,
            autoClose: 2000,
          });
          dispatch(
            createDailyTalktimeTopup(team, subData, (err) => {
              if (!err) dispatch(getAllDailyTalktimes(team.id));
            }),
          );
        }
      });
    }
    if (!team || !plan || !variant) {
      toast.error("Please select team & plan & variant", {
        transition: Slide,
        hideProgressBar: true,
        autoClose: 2000,
      });
      return;
    }
    if (!team?.is_lite && !team?.superfone_number && type !== "TOUPUP") {
      MySwal.fire({
        title: `Cannot ${
          order_type === "PURCHASE" ? "assign" : "renew"
        } plan to Org as it does not have a superfone number`,
        text: "",
      });
      return;
    }
    if (!team.email && (order_type === "PURCHASE" || order_type === "RENEW")) {
      await setOrgEmail({
        org_id: team.id,
        updateApi: async (email) => {
          try {
            if (email) {
              await updateOrg({
                org_id: team.id,
                email,
              });
              await addOrgNote({
                org_id: team.id,
                action_name: "Updated Org",
                comment: `setting email to ${email}`,
              });
              return true;
            } else {
              return false;
            }
          } catch (e) {
            return false;
          }
        },
      });
    }

    MySwal.fire({
      icon: "warning",
      title: "Are you sure?",
      text: `You want to create ${order_type} subscription for team id: ${
        team.id
      } name: ${team.name} plan: ${plan.name} ${
        variant?.name ? `duration: ${variant.name} ` : ""
      }Rs: ${
        payment_type === "BANK_TRANSFER"
          ? amount
          : (variant.amount / 100).toFixed(2)
      } with Payment Type ${payment_type}`,
      confirmButtonText: "YES! CREATE",
      showCancelButton: true,
      customClass: {
        confirmButton: "btn btn-danger",
        cancelButton: "btn btn-secondary ml-1",
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        toast.info("Creating Subscription...Please wait", {
          transition: Slide,
          hideProgressBar: true,
          autoClose: 2000,
        });
        dispatch(
          createSubscription(team, subData, (err) => {
            if (!err) {
              dispatch(getAllDailyTalktimes(team.id));
            }
          }),
        );
      }
    });
  };

  return (
    <Fragment>
      <Button.Ripple color="primary" onClick={handleOnClickCreateSub}>
        {topupOnly ? "Talktime Topup" : "Create Plan"}
      </Button.Ripple>
      {shouldShowCreateSubModal && (
        <CreateSubModal
          visible={shouldShowCreateSubModal}
          onClose={onCloseModel}
          onDismiss={onCloseModel}
          onProceed={onCreateSubInputComplete}
          currentSubscription={currentSubscription}
          team={team}
          topupOnly={topupOnly}
        />
      )}
    </Fragment>
  );
};

export default CreateSubscription;
